import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API_URL from '../config/api';
import AuthShell, { inputClass, primaryButtonClass, ErrorBox } from '../components/AuthShell';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const { uid, token } = useParams<{ uid: string; token: string }>();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(t('reset.mismatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/password-reset/confirm/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, token, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || data.password?.[0] || t('reset.invalid'));
      }
      setDone(true);
    } catch (err: any) {
      setError(err.message || t('common.genericError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t('reset.setTitle')}>
      {done ? (
        <div className="bg-white shadow rounded-lg p-6 text-center space-y-4">
          <p className="text-sm text-gray-700">{t('reset.done')}</p>
          <Link to="/login" className={primaryButtonClass}>{t('auth.signIn')}</Link>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <ErrorBox message={error} />
          <input
            type="password"
            required
            minLength={8}
            className={inputClass}
            placeholder={t('reset.newPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <input
            type="password"
            required
            minLength={8}
            className={inputClass}
            placeholder={t('reset.confirmPassword')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
          <p className="text-xs text-gray-500">{t('signup.passwordHint')}</p>
          <button type="submit" disabled={loading} className={primaryButtonClass}>
            {loading ? t('common.saving') : t('reset.setPassword')}
          </button>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
